import { HttpClient } from '@angular/common/http';
import { ElementRef, Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
    IIqTrendlinesGraphRatesNtlStateService,
    Rate,
    RateMap,
    TL_GRAPH_RATES_NATIONAL_URL,
    TRENDLINES_CACHE_DB,
    TRENDLINES_GRAPH_RATES_NATIONAL_FLATBED_DOC,
    TRENDLINES_GRAPH_RATES_NATIONAL_REEFER_DOC,
    TRENDLINES_GRAPH_RATES_NATIONAL_VAN_DOC,
} from '../../models';
import { DatDbClientService } from '@dat/db';
import * as FlatbedGraph from './graph-flatbed';
import * as ReeferGraph from './graph-reefer';
import * as VanGraph from './graph-van';
import { DateTime } from 'luxon';
import { DatiqAppConfig } from '@dat/datiq-core/models';
import { isNil } from 'lodash';

type NationalRateResponse = {
    response: {
        rates: {
            year: number;
            month: number;
            rateUSD: number;
            averageFuelSurchargePerTripUsd: number;
        }[];
    };
};

type RateCache = {
    contract: Rate[];
    spot: Rate[];
    expiryDate: string;
};

const RATES_DOC = new Map<string, string>([
    ['VAN', TRENDLINES_GRAPH_RATES_NATIONAL_VAN_DOC],
    ['REEFER', TRENDLINES_GRAPH_RATES_NATIONAL_REEFER_DOC],
    ['FLATBED', TRENDLINES_GRAPH_RATES_NATIONAL_FLATBED_DOC],
]);

@Injectable({
    providedIn: 'root',
})
export class IqTrendlinesGraphRatesNtlStateService
    implements IIqTrendlinesGraphRatesNtlStateService
{
    private _baseUrl: string =
        this._getAnalyticsUrl() + inject(TL_GRAPH_RATES_NATIONAL_URL);
    private _db = inject(DatDbClientService);
    private DB_OPTS: { dbName: string } = {
        dbName: TRENDLINES_CACHE_DB,
    };
    private PACIFIC_TIME_ZONE = 'America/Los_Angeles';
    constructor(
        private _httpClient: HttpClient,
        private _appConfig: DatiqAppConfig,
    ) {}

    private _getAnalyticsUrl(): string {
        return this._appConfig?.coreConfig?.ANALYTICS_BASE_URL ?? '';
    }

    private _isDataStale(expiryDate: string): boolean {
        // The Dates are stored in JS Date format, we need to convert them to Pacific Local Time
        const pacificExpiryDate: DateTime = DateTime.fromISO(
            expiryDate,
        ).setZone(this.PACIFIC_TIME_ZONE);
        if(isNil(pacificExpiryDate)) return true;
        const currentPacificTime: DateTime = DateTime.utc().setZone(
            this.PACIFIC_TIME_ZONE,
        );
        return currentPacificTime > pacificExpiryDate;
    }

    private _nextDay(): Date {
        //Next day at 7am
        return DateTime.utc()
            .setZone(this.PACIFIC_TIME_ZONE)
            .plus({ days: 1 })
            .set({
                hour: 7,
                minute: 0,
                second: 0,
                millisecond: 0,
            })
            .toJSDate();
    }

    async vanData(): Promise<RateMap> {
        return this.getRates('VAN');
    }

    async reeferData(): Promise<RateMap> {
        return this.getRates('REEFER');
    }

    async flatbedData(): Promise<RateMap> {
        return this.getRates('FLATBED');
    }

    async vanGraph(container: ElementRef<any>, rateMap: RateMap) {
        const { contract, spot } = rateMap;
        VanGraph.render(container, {
            contract,
            spot,
        });
    }
    async reeferGraph(container: ElementRef<any>, rateMap: RateMap) {
        const { contract, spot } = rateMap;
        ReeferGraph.render(container, {
            contract,
            spot,
        });
    }
    async flatbedGraph(container: ElementRef<any>, rateMap: RateMap) {
        const { contract, spot } = rateMap;
        FlatbedGraph.render(container, {
            contract,
            spot,
        });
    }

    private async getRates(equipment: string): Promise<RateMap> {
        const docName: string = RATES_DOC.get(equipment);
        const hasDoc: boolean = await lastValueFrom(
            this._db.hasDoc(docName, this.DB_OPTS),
        );

        if (!hasDoc) {
            const data: RateMap = await this._fetchAndCacheRates(
                equipment,
                docName,
            );
            return data;
        }
        const cachedData: RateCache = (await lastValueFrom(
            this._db.getDoc(docName, this.DB_OPTS),
        )) as RateCache;

        if (this._isDataStale(cachedData.expiryDate)) {
            return this._fetchAndCacheRates(equipment, docName);
        }
        return this._parseCachedData(cachedData);
    }

    private async _fetchAndCacheRates(
        equipment: string,
        docName: string,
    ): Promise<RateMap> {
        const spot: Rate[] = await this._fetchRates(equipment, 'SPOT');
        const contract: Rate[] = await this._fetchRates(equipment, 'CONTRACT');
        this._setRatesCache(docName, spot, contract);
        return { spot, contract };
    }

    private _fetchRates(equipment: string, rateType: string): Promise<Rate[]> {
        return lastValueFrom(
            this._httpClient.post(this._baseUrl, {
                equipment,
                rateType,
                periodType: 'Month',
                country: 'US',
            }),
        )
            .then((data: NationalRateResponse) => {
                return data?.response?.rates
                    ?.reverse()
                    ?.slice(0, 4)
                    ?.map((rate) => ({
                        date: new Date(`${rate.year}/${rate.month}/01`),
                        rate:
                            rate.rateUSD + rate.averageFuelSurchargePerTripUsd,
                    }))
                    ?.reverse();
            })
            .catch(() => {
                console.error(`Unable to Fetch ${equipment} ${rateType} Rates`);
                return [];
            });
    }

    private _setRatesCache(docName: string, spot: Rate[], contract: Rate[]) {
        const expiryDate = this._nextDay();
        const cache: RateCache = {
            spot,
            contract,
            expiryDate: expiryDate.toISOString(),
        };
        this._db.setDoc(docName, cache, this.DB_OPTS);
    }

    private _parseCachedData(doc: RateMap): RateMap {
        return {
            contract: doc.contract.map((rate) => ({
                date: new Date(rate.date),
                rate: rate.rate,
            })),
            spot: doc.spot.map((rate) => ({
                date: new Date(rate.date),
                rate: rate.rate,
            })),
        };
    }
}
